import { useCallback, useEffect, useRef } from 'react';
import type { BaseFieldErrorProps } from '../components/types';
import { useFormStatic } from './FormContext';
import { useField } from 'formik';

export interface useFormFieldResult<T> {
	name: string;
	isReadOnly: boolean;
	error?: BaseFieldErrorProps;
	isInvalid: boolean;
	value: T;
	onChange: (value: T) => Promise<void>;
	onBlur: () => void;
	onReset: () => Promise<void>;
	label: string;
	required: boolean;
}

function describeConstraint(params: Record<string, unknown> | undefined): string {
	if (!params) return '';
	const parts: string[] = [];
	if (params.min !== undefined) parts.push(`Minimum: ${params.min}`);
	if (params.max !== undefined) parts.push(`Maximum: ${params.max}`);
	if (params.length !== undefined) parts.push(`Expected length: ${params.length}`);
	return parts.join(' · ');
}

export function useFormField<T>(props: {
	name: string;
	label?: string;
	isReadOnly?: boolean;
	onChange?: (value: T) => void;
	validateOnChange?: boolean;
}): useFormFieldResult<T> {
	const { readOnly, fieldLabels, initialValues } = useFormStatic();
	const fieldName = props.name;
	const [field, meta, helpers] = useField<T>(fieldName);

	const helpersRef = useRef(helpers);
	const propsOnChangeRef = useRef(props.onChange);

	useEffect(() => {
		helpersRef.current = helpers;
		propsOnChangeRef.current = props.onChange;
	});

	const initialValueForField = initialValues[fieldName];

	const onChange = useCallback(
		async (value: T) => {
			await helpersRef.current.setValue(value, props.validateOnChange ?? false, true);
			propsOnChangeRef.current?.(value);
		},
		[props.validateOnChange]
	);

	const onReset = useCallback(async () => {
		await helpersRef.current.setValue(initialValueForField);
		await helpersRef.current.setTouched(false, false);
	}, [initialValueForField]);

	const onBlur = useCallback(async () => {
		await helpersRef.current.setTouched(true, true);
	}, []);

	const displayErrorMessage = meta.touched && meta.error && meta.error.length > 0 ? true : false;
	const errorMessage = meta.error!;

	let required = false;
	let fieldLabel = null;

	required = meta.params?.spec?.optional === false;
	fieldLabel = props.label ?? fieldLabels?.get(/\[\d+\]/.test(fieldName) ? fieldName.replace(/\[\d+\]/g, '[]') : fieldName);

	fieldLabel = fieldLabel ?? 'Invalid label';

	return {
		name: fieldName,
		isReadOnly: readOnly || (props.isReadOnly ?? false),
		error: displayErrorMessage
			? {
					errorMessage,
					errorDescription: describeConstraint(meta.params),
					errorCode: typeof meta.params?.type === 'string' ? meta.params.type : ''
			  }
			: undefined,
		isInvalid: displayErrorMessage,
		value: field.value,
		onChange,
		onBlur,
		onReset,
		label: fieldLabel,
		required
	};
}
